import { cloneDeep, compact, head, isArray, omit } from 'lodash';
import { type Model } from 'app/model/backendmodel';
import { NullWatermarkBehavior } from 'app/model/copyjob/copy-job.activity';
import { ChangeDetectionMethod, CopyJobMode, type CopyJobModeType } from 'app/model/copyjob/type';
import {
    CopySource,
    Dataset,
    FormatDatasetV2,
    ICopySinkWithUpdateMethod,
    LinkedService,
    Metamodel,
    Owned,
    stringIsNullOrEmpty,
    type TabularTypeDataset,
    UpdateMethod,
    UpdateMethodType
} from 'shell-models-lib';
import { type ArtifactJobHistory, type ArtifactJobInstance, ArtifactJobStatus } from '@trident/data-access-artifact';
import type { BlobTypeStoreReadSettings } from 'app/model/pipeline/copysource/store-read-settings/blob-type-store-read-settings';
import type { DatasetRegistryService } from 'app/registries/dataset/dataset-registry.service';
import type { LoggerService } from 'app/logger/logger.service';
import { DatasetUtils } from 'app/model/dataset/dataset.utils';
import type { FormatTypeCopySource } from 'app/model/pipeline/copysource/format-type.copysource';
import { ICopyJobRun, ICopyJobRunResult } from 'app/model/monitoring/monitoring-models';
import { LocalizationService } from 'app/services/localization.service';
import { convertStatus, getStatusIcon } from 'app/shared/utils/statusConverter.util';
import { formatDataSize } from 'app/shared/components/copy-activity-details-v2/util';
import { DateRangeManager } from 'app/model/monitoring/date-range-manager';
import { formatTimeWithRegionAndTimezone } from 'app/shared/utils/time-format.util';
import { IDatasetTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataset-type-metadata';
import { type ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { GlobalVariables } from 'app/services/globals';
import type { CopyJob } from 'app/model/copyjob/copy-job';
import { generateUUID } from 'app/shared/utils/uuid.util';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { partitionOptionConst } from 'app/registries/consts/metadata-consts';
import { isDateOnlyColumnType } from 'app/shared/utils/column-type.util';
import { type DeltaTier } from 'app/authoring/copy-job/wizard/copy-job-delta.const';
import {
    isDeltaCloneCopy,
    resolveDeltaSinkTier,
    sinkOnlySupportsDeltaFormat,
    type ICopyJobEndpointTypes
} from 'app/authoring/copy-job/wizard/copy-job-delta.util';

/**
 * Endpoint-supported formats, before copy-mode/read-method restrictions on the destination page.
 * A clone-only Delta source replaces the file-format list rather than extending it.
 */
export const getSupportedSinkFileFormats = (context: ICopyJobEndpointTypes): string[] => {
    const tier = resolveDeltaSinkTier(context);
    if (sinkOnlySupportsDeltaFormat(context)) {
        return [adfModelTypeMap.delta.datasetType];
    }
    return [
        adfModelTypeMap.delimitedText.datasetType,
        adfModelTypeMap.parquet.datasetType,
        adfModelTypeMap.json.datasetType,
        adfModelTypeMap.avro.datasetType,
        adfModelTypeMap.orc.datasetType
    ]
        .concat(tier !== 'none' ? [adfModelTypeMap.delta.datasetType] : [])
        .concat(
            context.sinkLinkedServiceType === adfModelTypeMap.azureBlobFS.linkedServiceType
                ? [adfModelTypeMap.iceberg.datasetType]
                : []
        )
        .sort((a, b) => a.localeCompare(b));
};

/** A clone can only merge; overwrite is dropped for incremental copies as it would discard copied changes. */
export const getDeltaSinkUpdateMethods = (
    tier: DeltaTier,
    mode: CopyJobModeType,
    changeDetectionMethod?: ChangeDetectionMethod
): UpdateMethodType[] => {
    if (tier === 'none') {
        return [];
    }
    if (tier === 'clone') {
        return [UpdateMethod.Upsert];
    }
    if (mode !== CopyJobMode.IncrementalCopy) {
        // A batch copy reads the whole table, so the change-detection method does not apply to it.
        return [UpdateMethod.Append, UpdateMethod.Upsert, UpdateMethod.Overwrite];
    }
    const methods = [UpdateMethod.Append, UpdateMethod.Upsert];
    // A native CDC copy carries updates and deletes, so it can only be applied by merging.
    return changeDetectionMethod === ChangeDetectionMethod.NATIVE_CDC
        ? methods.filter((method) => updateMethodSupportCDC(method))
        : methods;
};

/**
 * Such datasets store the update method and its `updateMethodProperties` under `copySink.formatSettings`
 * rather than on the `copySink`, and own the update-method UI next to the format picker.
 */
export const formatDatasetSupportsUpdateMethod = (sinkDatasetMetadata?: IDatasetTypeMetadata): boolean =>
    !!sinkDatasetMetadata?.isV2FormatDataset && !!sinkDatasetMetadata?.copyJob?.updateMethod?.supportUpdateMethod;

/**
 * The settings page is reached before the destination format is chosen, so ownership can only be decided from
 * the sink linked service: as soon as it can offer any format whose dataset carries its own update method,
 * the update method is configured next to that format instead.
 */
export const fileFormatPageOwnsUpdateMethod = (context: {
    supportedSinkFormatMetadata: IDatasetTypeMetadata[];
    isLakehouseTableDestination: boolean;
}): boolean =>
    !context.isLakehouseTableDestination &&
    context.supportedSinkFormatMetadata.some(formatDatasetSupportsUpdateMethod);

export const getLatestArtifactJobInstanceFromArtifactJobHistory = (
    artifactJobHistory: ArtifactJobHistory
): ArtifactJobInstance | undefined => {
    if (!isArray(artifactJobHistory?.history)) {
        return undefined;
    }

    return artifactJobHistory.history.reduce((previousValue, currentValue) => {
        const previousJobStartTimeUtc = new Date(previousValue.jobStartTimeUtc);
        const currentJobStartTimeUtc = new Date(currentValue.jobStartTimeUtc);
        if (
            currentJobStartTimeUtc.getTime() > previousJobStartTimeUtc.getTime() &&
            // skip duplicate status
            currentValue.status !== ArtifactJobStatus.Duplicate
        ) {
            return currentValue;
        }
        return previousValue;
    }, artifactJobHistory.history[0]);
};

export const isFileType = (dataset: Model.Dataset): boolean => dataset?.typeProperties?.location != null;

export const isFileTypeDataset = (datasetMetadata?: IDatasetTypeMetadata): boolean =>
    datasetMetadata?.isV2FormatDataset === true;

const generateDisplayNameFromTabularDataset = (dataset: TabularTypeDataset) =>
    compact([dataset.schema, dataset.table || dataset.tableName]).join('.');

const generateDisplayNameFromFileDataset = (dataset: FormatDatasetV2, storeSettings?: BlobTypeStoreReadSettings) => {
    const filePath = dataset.location.getLocation();
    if (
        !stringIsNullOrEmpty(storeSettings?.wildcardFolderPath) ||
        !stringIsNullOrEmpty(storeSettings?.wildcardFileName)
    ) {
        return compact([filePath.container, storeSettings.wildcardFolderPath, storeSettings.wildcardFileName]).join(
            '/'
        );
    }

    return filePath.fileName || filePath.getFullPath();
};

export const generateDisplayNameFromActivity = (
    entity: { sourceDataset?: Dataset; sinkDataset?: Dataset; source?: CopySource },
    datasetRegistryService?: DatasetRegistryService,
    logger?: LoggerService,
    defaultName = '-'
): [string, string] => {
    let sourceDisplayName = defaultName;
    let sinkDisplayName = defaultName;
    if (entity.sourceDataset) {
        try {
            let isSourceFileType: boolean;
            if (datasetRegistryService == null) {
                isSourceFileType = entity.sourceDataset instanceof FormatDatasetV2;
            } else {
                isSourceFileType = DatasetUtils.isFileBasedTypeDataset(entity.sourceDataset, {
                    dsSvc: datasetRegistryService
                });
            }
            sourceDisplayName = isSourceFileType
                ? generateDisplayNameFromFileDataset(
                      entity.sourceDataset as FormatDatasetV2,
                      (entity.source as FormatTypeCopySource)?.storeSettings as BlobTypeStoreReadSettings
                  )
                : generateDisplayNameFromTabularDataset(entity.sourceDataset as TabularTypeDataset);
        } catch (e) {
            if (logger) {
                logger.error('generateDisplayNameFromActivity', 'generate source default display name failed', {
                    reason: e
                });
            }
        }
    }

    if (entity.sinkDataset) {
        try {
            let isSinkFileType: boolean;
            if (datasetRegistryService == null) {
                isSinkFileType = entity.sinkDataset instanceof FormatDatasetV2;
            } else {
                isSinkFileType = DatasetUtils.isFileBasedTypeDataset(entity.sinkDataset, {
                    dsSvc: datasetRegistryService
                });
            }
            sinkDisplayName = isSinkFileType
                ? generateDisplayNameFromFileDataset(entity.sinkDataset as FormatDatasetV2)
                : generateDisplayNameFromTabularDataset(entity.sinkDataset as TabularTypeDataset);
        } catch (e) {
            if (logger) {
                logger.error('generateDisplayNameFromActivity', 'generate sink default display name failed', {
                    reason: e
                });
            }
        }
    }

    return [sourceDisplayName, sinkDisplayName];
};

const formatTimeWithTimeZone = (
    date: Date,
    timeZone: string = DateRangeManager.timeZone,
    services: {
        localizationService: LocalizationService;
        loggerService: LoggerService;
    }
) => {
    const { localizationService, loggerService } = services;
    const regionCode = localizationService.getLanguageRegionCode();
    return formatTimeWithRegionAndTimezone(date, regionCode, loggerService, timeZone);
};

export const getCopyJobRunDetailsWarning = (
    result: Pick<ICopyJobRunResult, 'detailsUnavailable' | 'detailsThrottled'> | undefined
): string | undefined => {
    if (!result?.detailsUnavailable) {
        return undefined;
    }
    return result.detailsThrottled
        ? stringResources.CopyJob_RunDetailsThrottled
        : stringResources.CopyJob_RunDetailsUnavailable;
};

export const convertRunResultToCopyJobRun = (
    result: ICopyJobRunResult,
    services: {
        localizationService: LocalizationService;
        loggerService: LoggerService;
    }
): ICopyJobRun => {
    const { filesRead, filesWritten, rowsRead, rowsCopied, dataRead, dataWritten, runStart, runEnd } = result;
    const defaultDisplay: '-' = '-';
    const status = convertStatus(result.status);
    return {
        ...omit(
            result,
            'runs',
            'status',
            'dataRead',
            'dataWritten',
            'filesRead',
            'filesWritten',
            'rowsRead',
            'rowsCopied'
        ),
        status,
        statusIcon: getStatusIcon(status),
        rowsRead: rowsRead == null ? defaultDisplay : rowsRead,
        rowsCopied: rowsCopied == null ? defaultDisplay : rowsCopied,
        filesRead: filesRead == null ? defaultDisplay : filesRead,
        filesWritten: filesWritten == null ? defaultDisplay : filesWritten,
        dataRead: dataRead == null ? defaultDisplay : formatDataSize(dataRead),
        dataWritten: dataWritten == null ? defaultDisplay : formatDataSize(dataWritten),
        runStartStr: runStart ? formatTimeWithTimeZone(runStart, undefined, services) : undefined,
        runEndStr: runEnd ? formatTimeWithTimeZone(runEnd, undefined, services) : undefined
    };
};

export const convertTableActionOptionToWriteBehavior = (tableActionOption: string): 'Append' | 'Overwrite' | string => {
    switch (tableActionOption.toLocaleLowerCase()) {
        case 'append':
            return 'Append';
        case 'overwrite':
        case 'overwriteschema':
            return 'Overwrite';
        case 'upsert':
            return 'Upsert';
        case 'scd2':
            return 'SCD2';
        default:
            return tableActionOption;
    }
};

export const convertSQLWriteBehaviorToCopyJobWriteBehavior = (writeBehavior: string): 'Append' | 'Upsert' | string => {
    switch (writeBehavior.toLocaleLowerCase()) {
        case 'insert':
            return 'Append';
        case 'upsert':
            return 'Upsert';
        default:
            return writeBehavior;
    }
};

export const INCREMENTAL_COPY_ONLY_UPDATE_METHODS = new Set<UpdateMethodType>([UpdateMethod.Upsert, UpdateMethod.SCD2]);

export const NATIVE_CDC_UPDATE_METHODS = new Set<UpdateMethodType>([UpdateMethod.Upsert, UpdateMethod.SCD2]);

export const updateMethodSupportCDC = (updateMethod: UpdateMethodType): boolean => {
    return NATIVE_CDC_UPDATE_METHODS.has(updateMethod);
};

export const UPDATE_METHODS_NEED_UPSERT_SETTING = new Set<UpdateMethodType>([UpdateMethod.Upsert, UpdateMethod.SCD2]);

type CustomStagingSchemaSettings = {
    updateMethod: UpdateMethodType | undefined;
    fullLoadBehavior: CopyJob['fullLoadBehavior'] | undefined;
    isDeltaClone: boolean;
};

/**
 * Returns whether the custom "Schema name for staging tables" input should be
 * shown / persisted. The setting is only meaningful when the destination is
 * actually going to stage rows: SCD2, upsert(merge), or Truncate + Append/Upsert.
 * Delta clone copies do not stage rows, even though they use Merge.
 *
 * Keep this as the single gate for display, save, and stale-cache cleanup.
 * Callers with a CopyJob can pass the entity directly; callers that only have
 * wizard cache data can pass the normalized settings to reuse the same logic.
 */
export const shouldShowCustomStagingSchema = (
    entityOrSettings: CopyJob | CustomStagingSchemaSettings
): boolean => {
    if (!GlobalVariables.copyJobCustomStagingSchema) {
        return false;
    }
    const isDeltaClone = 'activities' in entityOrSettings
        ? Array.from(entityOrSettings.activities).some(isDeltaCloneCopy)
        : entityOrSettings.isDeltaClone;
    if (isDeltaClone) {
        return false;
    }
    const settings = 'activities' in entityOrSettings
        ? {
            updateMethod: (head(Array.from(entityOrSettings.activities))?.copySink as ICopySinkWithUpdateMethod)?.updateMethod,
            fullLoadBehavior: entityOrSettings.fullLoadBehavior
        }
        : entityOrSettings;
    const isTruncateWatermarkLoad =
        settings.fullLoadBehavior === 'Truncate' &&
        (settings.updateMethod === UpdateMethod.Append || settings.updateMethod === UpdateMethod.Upsert);
    return (
        settings.updateMethod === UpdateMethod.SCD2 ||
        settings.updateMethod === UpdateMethod.Upsert ||
        isTruncateWatermarkLoad
    );
};

export function isCopyJobSourceFile(options: {
    linkedServiceMetadata?: ILinkedServiceTypeMetadata;
    datasetMetadata?: IDatasetTypeMetadata;
}): boolean {
    const { linkedServiceMetadata, datasetMetadata } = options;
    if (linkedServiceMetadata && datasetMetadata) {
        return linkedServiceMetadata.type === adfModelTypeMap.lakehouse.linkedServiceType
            ? datasetMetadata.type !== adfModelTypeMap.lakehouseTable.datasetType
            : linkedServiceMetadata.linkedServiceKind === 'file';
    }
    return false;
}

export { NullWatermarkBehavior };

export function shouldShowNullWatermarkBehaviorControl(options: {
    sourceIsTabular: boolean;
    hasIncrementalColumns: boolean;
}): boolean {
    return options.sourceIsTabular && options.hasIncrementalColumns;
}

export function validateNullWatermarkBehaviorWithPartition(options: {
    nullWatermarkBehavior?: NullWatermarkBehavior;
    partitionOption?: string;
    message: string;
}): string | undefined {
    if (
        options.nullWatermarkBehavior === NullWatermarkBehavior.IncludeOnFullLoad &&
        options.partitionOption === partitionOptionConst.autoDetect
    ) {
        return options.message;
    }
    return undefined;
}

export const generateUpdateMethodOptions = (
    copyJobSettings: IDatasetTypeMetadata['copyJob'],
    mode: CopyJobModeType,
    {
        updateMethod,
        nativeCDCOnly,
        supportNativeCDC,
        sourceDatasetType,
        isDeltaClone,
        availableUpdateMethods
    }: {
        updateMethod?: UpdateMethodType;
        nativeCDCOnly?: boolean;
        supportNativeCDC?: boolean;
        sourceDatasetType?: string;
        isDeltaClone?: boolean;
        availableUpdateMethods?: UpdateMethodType[];
    } = {}
) => {
    let options = cloneDeep(copyJobSettings?.updateMethod?.options) ?? [];
    if (isArray(availableUpdateMethods) && availableUpdateMethods.length > 0) {
        options = options.filter((option) => availableUpdateMethods.includes(option.value));
    }
    if (GlobalVariables.enableCJBatchUpsert) {
        return options;
    }
    if (mode === CopyJobMode.FullCopy && !isDeltaClone && updateMethod !== UpdateMethod.Upsert && !copyJobSettings?.updateMethod?.supportUpsertInBatch) {
        const upsertOption = options.find((option) => option.value === UpdateMethod.Upsert);
        if (upsertOption) {
            upsertOption.disabled = true;
            upsertOption.disabledTooltip = stringResources.CopyJob_MergeDisabledInFullCopyTooltip;
        }
    }
    if (mode === CopyJobMode.IncrementalCopy && updateMethod !== UpdateMethod.Overwrite) {
        const overwriteOption = options.find((option) => option.value === UpdateMethod.Overwrite);
        if (overwriteOption) {
            overwriteOption.disabled = true;
            overwriteOption.disabledTooltip = stringResources.CopyJob_OverwriteDisabledInIncrementalCopyTooltip;
        }
    }
    // Snowflake source does not support SCD2
    if (sourceDatasetType !== adfModelTypeMap.snowflakeV2.datasetType) {
        if (mode !== CopyJobMode.IncrementalCopy) {
            const scd2Option = options.find((option) => option.value === UpdateMethod.SCD2);
            if (scd2Option) {
                scd2Option.disabled = true;
                scd2Option.disabledTooltip = stringResources.CopyJob_SCD2DisabledInBatchCopyTooltip;
            }
        } else if (!supportNativeCDC) {
            const scd2Option = options.find((option) => option.value === UpdateMethod.SCD2);
            if (scd2Option) {
                scd2Option.disabled = true;
                scd2Option.disabledTooltip = stringResources.CopyJob_SCD2OnlySupportedInNativeCDCTooltip;
            }
        }
    } else {
        options = options.filter((option) => option.value !== UpdateMethod.SCD2);
    }
    if (nativeCDCOnly) {
        options.forEach((option) => {
            switch (option.value) {
                case UpdateMethod.Append:
                    option.disabled = true;
                    option.disabledTooltip = stringResources.CopyJob_AppendDisabledInCDCCopyTooltip;
                    break;
                case UpdateMethod.Overwrite:
                    option.disabled = true;
                    option.disabledTooltip = stringResources.CopyJob_OverwriteDisabledInCDCCopyTooltip;
                    break;
                default:
            }
        });
    }
    return options;
};

export const generateTemplateLinkedService = (linkedService: LinkedService, owner?: Owned): LinkedService => {
    const linkedServiceModel = Metamodel.modelOf(linkedService);
    const templateLinkedService = Owned.create<LinkedService>(linkedServiceModel, owner);
    templateLinkedService.tridentArtifactType = linkedService.tridentArtifactType;
    templateLinkedService.tridentConnectionKind = linkedService.tridentConnectionKind;
    templateLinkedService.name = generateUUID().replaceAll('-', '_');
    templateLinkedService.parameterized = true;
    return templateLinkedService;
};

export const VIRTUAL_SCROLL_THRESHOLD = 100;

export interface ISapTableAbapDateWatermarkParams {
    sourceLinkedServiceType?: string;
    useAbap?: boolean;
    mode?: CopyJobModeType;
    updateMethod?: UpdateMethodType;
    sourceDatasetType?: string;
    /** `schema` is `unknown` because callers hold either a column array or a hierarchical schema object. */
    incrementalColumns: Array<{ incrementalColumn?: string; incrementalColumn2?: string; schema?: unknown }>;
}

/**
 * SAP Table with the ABAP Add-On cannot use the Append update method when any watermark
 * column is date-typed. Shared by the wizard settings page and the advanced-settings
 * read-method tab so both validation paths stay in sync.
 */
export const hasUnsupportedSapTableAbapDateWatermark = (params: ISapTableAbapDateWatermarkParams): boolean => {
    if (
        params.sourceLinkedServiceType !== adfModelTypeMap.sapTable.linkedServiceType ||
        !params.useAbap ||
        params.mode !== CopyJobMode.IncrementalCopy ||
        params.updateMethod !== UpdateMethod.Append
    ) {
        return false;
    }
    return params.incrementalColumns.some(({ incrementalColumn, incrementalColumn2, schema }) => {
        if (!isArray(schema)) {
            return false;
        }
        const columns = schema as Array<{ name: string; type?: string; nativeDataType?: string }>;
        return [incrementalColumn, incrementalColumn2].some((name) => {
            if (stringIsNullOrEmpty(name)) {
                return false;
            }
            const column = columns.find((col) => col.name === name);
            return column != null && isDateOnlyColumnType(column.type, column.nativeDataType, params.sourceDatasetType);
        });
    });
};
